/**
 * Loads Sentry from the official browser CDN when REACT_APP_SENTRY_DSN is set.
 * The browser SDK is not an npm dependency; init completes before the app root is rendered.
 */

import { env, nodeEnv } from "./config/env";
const SENTRY_BUNDLE = "https://browser.sentry-cdn.com/8.55.0/bundle.min.js";
const SENTRY_SCRIPT_ID = "lw-sentry-browser-bundle";

function injectScript(src) {
  return new Promise((resolve, reject) => {
    const existing = typeof document !== "undefined" ? document.getElementById(SENTRY_SCRIPT_ID) : null;
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = SENTRY_SCRIPT_ID;
    script.src = src;
    script.crossOrigin = "anonymous";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function ensureSentryLoaded() {
  const dsn = env.sentryDsn;
  if (!dsn || typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.__LYW_SENTRY_READY__) {
    return Promise.resolve();
  }

  if (window.__LYW_SENTRY_LOADING__) {
    return window.__LYW_SENTRY_LOADING__;
  }

  const environment = env.sentryEnvironment || nodeEnv || "production";
  const release = env.sentryRelease || env.commitSha || undefined;

  window.__LYW_SENTRY_LOADING__ = injectScript(SENTRY_BUNDLE)
    .then(() => {
      if (window.Sentry && typeof window.Sentry.init === "function") {
        window.Sentry.init({
          dsn,
          environment,
          release,
          sendDefaultPii: false,
          beforeSend(event, hint) {
            const msg =
              event?.message ||
              hint?.originalException?.message ||
              hint?.originalException?.toString?.() ||
              "";
            // Common noisy browser issue; not actionable for us.
            if (String(msg).includes("ResizeObserver loop limit exceeded")) return null;
            return event;
          },
        });
        try {
          window.Sentry.setTag("runtime", "browser");
        } catch {
          /* ignore */
        }
      }
      window.__LYW_SENTRY_READY__ = true;
    })
    .catch((err) => {
      console.warn("[Sentry]", err);
      window.__LYW_SENTRY_READY__ = true;
    })
    .finally(() => {
      window.__LYW_SENTRY_LOADING__ = null;
    });

  return window.__LYW_SENTRY_LOADING__;
}
